import { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Skeleton, Snackbar, TextField } from "@mui/material";
import { authData } from "../../components/GetAuth";
import billingStyles from "../../components/billing.module.css";
import styles from "./superadmin.module.css";

// Platform API calls (v1/admin/*), same axios + bearer pattern as the rest of the app
const headers = () => ({ Authorization: `Bearer ${authData()?.api_token}` });
export const saGet = (path, params) => axios.get(process.env.REACT_APP_API_URL + "v1/admin/" + path, { params, headers: headers() });
export const saPost = (path, data) => axios.post(process.env.REACT_APP_API_URL + "v1/admin/" + path, data || {}, { headers: headers() });
export const saPut = (path, data) => axios.put(process.env.REACT_APP_API_URL + "v1/admin/" + path, data || {}, { headers: headers() });

export const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
export const fmtDateTime = (d) => (d ? dayjs(d).format("DD MMM YYYY, HH:mm") : "—");

const CHIP = { trial: "ChipTrial", active: "ChipActive", suspended: "ChipSuspended", cancelled: "ChipVoid", pending: "ChipPending", overdue: "ChipOverdue", paid: "ChipPaid", void: "ChipVoid" };
const CHIP_LABEL = { trial: "Trial", active: "Active", suspended: "Suspended", cancelled: "Cancelled", pending: "Pending", overdue: "Overdue", paid: "Paid", void: "Void" };

export const StatusChip = ({ status, label }) => (<span className={`${billingStyles.Chip} ${billingStyles[CHIP[status]] || ""}`}>{label || CHIP_LABEL[status] || status}</span>);

export const Kpi = ({ label, value, sub, icon, danger }) => (<div className={`${styles.Kpi} ${danger ? styles.KpiDanger : ""}`}>
  <span className={`${styles.KpiLabel}`}>{icon}{label}</span>
  <span className={`${styles.KpiValue}`}>{value}</span>
  {sub && <span className={`${styles.KpiSub}`}>{sub}</span>}
</div>);

export const TableSkeleton = ({ rows = 6, kpis = 0 }) => (<div aria-busy="true" aria-label="Loading">
  {kpis > 0 && <div className={`${styles.KpiGrid}`}>
    {Array.from({ length: kpis }).map((_, i) => <Skeleton key={i} variant="rounded" height={92} sx={{ borderRadius: "14px" }} />)}
  </div>}
  <Skeleton variant="rounded" height={44} sx={{ borderRadius: "10px", mb: 2, maxWidth: 360 }} />
  {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: "8px", mb: 1 }} />)}
</div>);

// Human text for a billing event (activity log, timelines)
const EVENT_TEXT = {
  trial_started: (m) => `Trial started, ends ${fmtDate(m?.trial_ends_at)}`,
  trial_extended: (m) => (m?.days ? `Trial extended by ${m.days} days (ends ${fmtDate(m?.trial_ends_at)})` : `Trial end moved to ${fmtDate(m?.trial_ends_at)}${m?.from ? ` (was ${fmtDate(m.from)})` : ""}`),
  cancel_requested: (m) => `Unsubscribed, service ends ${fmtDate(m?.cancels_at)}${m?.reason ? ` (reason: ${m.reason})` : ""}`,
  cancel_undone: () => "Cancellation undone, subscription continues",
  cancelled: () => "Subscription ended",
  resubscribed: () => "Resubscribed",
  addon_enabled: (m) => `${m?.name || m?.key} enabled`,
  addon_disabled: (m) => `${m?.name || m?.key} disabled`,
  invoice_generated: (m) => `Invoice ${m?.number} generated (£${Number(m?.total || 0).toFixed(2)})`,
  invoice_emailed: (m) => `Invoice emailed to ${m?.to}`,
  email_failed: (m) => `Invoice email failed: ${m?.error}`,
  due_notice: (m) => `"Payment due" notice sent for ${m?.number}`,
  final_notice: (m) => `Final notice sent for ${m?.number}`,
  suspended: (m) => `Service suspended (${m?.reason === "manual" ? "by admin" : "overdue invoice"})`,
  reactivated: () => "Service reactivated",
  paid: (m) => `Invoice ${m?.number} marked paid${m?.reference ? ` (ref ${m.reference})` : ""}`,
  voided: (m) => `Invoice ${m?.number} voided`,
  price_changed: (m) => `${m?.item} price changed £${Number(m?.from || 0).toFixed(2)} → £${Number(m?.to || 0).toFixed(2)}`,
  pricing_changed: (m) => (m?.to === "Free" ? `${m?.item} given free` : m?.to ? `${m?.item} discount set to ${m.to} (now £${Number(m?.price || 0).toFixed(2)}/mo)` : `${m?.item} back to standard price${m?.from ? ` (was ${m.from === "Free" ? "free" : m.from})` : ""}`),
  note: (m) => `Note: ${m?.note}`,
};

export const EVENT_TYPES = Object.keys(EVENT_TEXT);

export const eventText = (e) => (EVENT_TEXT[e.type] ? EVENT_TEXT[e.type](e.meta || {}) : e.type);

export const eventTone = (type) => {
  if (["paid", "reactivated", "addon_enabled", "invoice_emailed", "trial_started", "cancel_undone", "resubscribed"].includes(type)) return styles.TimelineGood;
  if (["suspended", "email_failed", "final_notice", "voided", "addon_disabled", "cancel_requested", "cancelled"].includes(type)) return styles.TimelineBad;
  return styles.TimelineInfo;
};

// Record an offline payment against an invoice
export const MarkPaidDialog = ({ invoice, onClose, onDone, showMessage }) => {
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [method, setMethod] = useState("Bank transfer");
  const [saving, setSaving] = useState(false);

  const submit = () => {
    setSaving(true);
    saPost(`invoices/${invoice.id}/mark-paid`, { reference, paid_at: paidAt, method }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        showMessage(response.data.message);
        onDone(response.data.data);
        onClose();
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch(() => { setSaving(false); showMessage("Could not record the payment.", "error"); });
  };

  return (<Dialog open={!!invoice} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
    <DialogTitle>Mark {invoice?.invoice_number} as paid</DialogTitle>
    <DialogContent>
      <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>£{Number(invoice?.total || 0).toFixed(2)} from {invoice?.company_name || "this company"}. If the company is suspended for this invoice, service is restored straight away.</p>
      <TextField label="Payment reference" value={reference} onChange={(e) => setReference(e.target.value)} fullWidth margin="dense" size="small" />
      <TextField label="Paid on" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} fullWidth margin="dense" size="small" InputLabelProps={{ shrink: true }} />
      <TextField label="Method" select value={method} onChange={(e) => setMethod(e.target.value)} fullWidth margin="dense" size="small">
        {["Bank transfer", "Card", "Cash", "Cheque", "Other"].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
      </TextField>
    </DialogContent>
    <DialogActions sx={{ padding: "0 24px 20px" }}>
      <button type="button" className={`${billingStyles.SecondaryBU}`} disabled={saving} onClick={onClose}>Cancel</button>
      <button type="button" className={`${billingStyles.PrimaryBU}`} disabled={saving} onClick={submit}>{saving ? "Saving…" : "Mark paid"}</button>
    </DialogActions>
  </Dialog>);
};

// Small confirm dialog for void / suspend / generate actions
export const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", onConfirm, onClose, busy }) => (<Dialog open={!!open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
  <DialogTitle>{title}</DialogTitle>
  <DialogContent><p style={{ fontSize: 14, lineHeight: "22px", color: "#4b5563", margin: 0 }}>{message}</p></DialogContent>
  <DialogActions sx={{ padding: "0 24px 20px" }}>
    <button type="button" className={`${billingStyles.SecondaryBU}`} disabled={busy} onClick={onClose}>Cancel</button>
    <button type="button" className={`${billingStyles.PrimaryBU}`} disabled={busy} onClick={onConfirm}>{busy ? "Working…" : confirmLabel}</button>
  </DialogActions>
</Dialog>);

// Snackbar state helper used by every platform page
export const useToast = () => {
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });
  const showMessage = (message, type = "success") => setToast({ open: true, message, type });
  const close = () => setToast((t) => ({ ...t, open: false }));
  return [toast, showMessage, close];
};

export const Toast = ({ toast, onClose }) => (<Snackbar open={toast.open} autoHideDuration={6000} onClose={onClose} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
  <Alert onClose={onClose} severity={toast.type} variant="filled" sx={{ width: "100%" }}>{toast.message}</Alert>
</Snackbar>);
